import React from 'react';
import { Icons } from '../../Icon.jsx';
import { DraggableLabel } from '../../DraggableLabel.jsx';
import { getThemeColorsMap } from '../../../utils/theme-helper.js';
import BindingList from './BindingList.jsx';
import PropertyControl from '../../VariantEditorDialog/components/PropertyControl.jsx';
import VariantSelector from '../../VariantSelector.jsx';
import MenuPickerField from './MenuPickerField.jsx';
import MediaPickerField from './MediaPickerField.jsx';

const FieldRenderer = ({ 
    label, 
    value, 
    onChange, 
    type = 'text', 
    options, 
    fieldName, 
    compId = 'native', 
    step, 
    componentType, 
    disabledList, 
    onUpdateDisabled,
    // Context
    primaryNode,
    isBindingEnabled,
    bindings = [],
    onBindingClick,
    onVariantEdit, // { isOpen, componentType, initialKey }
    // Responsive Context
    isOverridden = false,
    activeBreakpoint = 'default',
    // Binding List Props (passed down)
    bindingListProps = {} 
}) => {
      
      // Media picker (image from media library)
      if (type === 'media_picker') {
          return (
              <div className="mb-2">
                  <label className="text-[10px] text-slate-500 block mb-1 font-semibold">{label}</label>
                  <MediaPickerField value={value} onChange={onChange} />
              </div>
          );
      }

      // Menu configuration picker (CMS menu dropdown + inline item editor)
      if (type === 'menu_config') {
          return (
              <div className="mb-2">
                  <label className="text-[10px] text-slate-500 block mb-1 font-semibold">{label}</label>
                  <MenuPickerField value={value} onChange={onChange} />
              </div>
          );
      }

      // Special Handling for DataBinding List
      if (type === 'binding_list') {
          // Find the actual component object
          const comp = primaryNode.attachedComponents?.find(c => c.id === compId);
          if (comp) return <BindingList comp={comp} {...bindingListProps} />;
          return null;
      }

      if (type === 'variant_list') {
          const comp = primaryNode.attachedComponents?.find(c => c.id === compId);
          const currentDisabled = disabledList || (comp ? (comp.props.disabledVariants || []) : []);

          const handleUpdateDisabled = (newDisabled) => {
              if (onUpdateDisabled) {
                  onUpdateDisabled(newDisabled);
              } else if (comp && bindingListProps.onUpdateBindings) {
                  bindingListProps.onUpdateBindings(comp.id, 'disabledVariants', newDisabled);
              }
          };

          return (
              <VariantSelector
                  value={value}
                  onChange={onChange}
                  disabledList={currentDisabled}
                  onUpdateDisabled={handleUpdateDisabled}
                  onVariantEdit={onVariantEdit}
                  componentType={componentType}
                  label={label}
              />
          );
      }

      if (type === 'h_align') {
          return (
             <div className="mb-2">
                 <label className="text-[10px] text-slate-500 block mb-1 font-semibold">{label}</label>
                 <div className="flex bg-slate-100 p-0.5 rounded border border-slate-200">
                    {[
                        { val: 'left', icon: Icons.AlignLeft },
                        { val: 'center', icon: Icons.AlignCenter },
                        { val: 'right', icon: Icons.AlignRight }
                    ].map(btn => (
                        <button
                            key={btn.val}
                            onClick={() => onChange(value === btn.val ? null : btn.val)}
                            className={`flex-1 flex items-center justify-center py-1 rounded hover:bg-white hover:shadow-sm transition-all ${value === btn.val ? 'bg-white shadow text-blue-600' : 'text-gray-400'}`}
                            title={btn.val}
                        >
                            <btn.icon className="w-3.5 h-3.5" />
                        </button>
                    ))}
                </div>
             </div>
          );
      }

      if (type === 'v_align') {
          return (
             <div className="mb-2">
                 <label className="text-[10px] text-slate-500 block mb-1 font-semibold">{label}</label>
                 <div className="flex bg-slate-100 p-0.5 rounded border border-slate-200">
                    {[
                        { val: 'top', icon: Icons.AlignTop },
                        { val: 'middle', icon: Icons.AlignMiddle },
                        { val: 'bottom', icon: Icons.AlignBottom }
                    ].map(btn => (
                        <button
                            key={btn.val}
                            onClick={() => onChange(value === btn.val ? null : btn.val)}
                            className={`flex-1 flex items-center justify-center py-1 rounded hover:bg-white hover:shadow-sm transition-all ${value === btn.val ? 'bg-white shadow text-blue-600' : 'text-gray-400'}`}
                            title={btn.val}
                        >
                            <btn.icon className="w-3.5 h-3.5" />
                        </button>
                    ))}
                </div>
             </div>
          );
      }

      // Check Binding Status
      let isBound = false;
      let boundKey = undefined;
      if (isBindingEnabled && fieldName) {
           const binding = bindings.find(b => b.nodeId === primaryNode.id && b.componentId === compId && b.property === fieldName);
           if (binding) {
               isBound = true;
               boundKey = binding.externalKey;
           }
      }

      const handleLabelClick = (e) => {
          if (!isBindingEnabled || !fieldName) return;
          e.stopPropagation();
          const rect = e.target.getBoundingClientRect();
          onBindingClick({
              x: rect.left,
              y: rect.bottom + 5,
              nodeId: primaryNode.id,
              componentId: compId,
              property: fieldName,
              existingKey: boundKey
          });
      };

      const isInherited = activeBreakpoint !== 'default' && !isOverridden;
      const inputStyleClass = isOverridden 
          ? 'border-blue-400 bg-blue-50/20' 
          : (isInherited ? 'border-dashed border-slate-300 text-slate-400' : 'border-slate-200');

      return (
        <div className="mb-2 relative group">
            <div className="flex items-center gap-1.5 mb-1">
                <DraggableLabel
                    label={label}
                    value={value}
                    onChange={onChange}
                    onClick={handleLabelClick}
                    className={`text-[10px] flex items-center gap-1 w-fit select-none ${isBindingEnabled ? 'cursor-pointer hover:text-blue-600 transition-colors' : 'text-slate-500'}`}
                    title={isBindingEnabled ? (isBound ? `Bound to '${boundKey}'. Click to edit.` : 'Click to Bind') : 'Drag to adjust value'}
                >
                    {isBound && <div className="w-1.5 h-1.5 rounded-full bg-green-500" />}
                    {isOverridden && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" title={`Overridden in ${activeBreakpoint}`} />}
                    <span className={isBound ? 'text-green-600 font-semibold' : (isOverridden ? 'text-blue-600 font-semibold' : '')}>{label}</span>
                </DraggableLabel>
                {activeBreakpoint !== 'default' && isOverridden && (
                    <button
                        onClick={() => onChange(undefined)} // Reset override
                        className="text-[9px] text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Reset Override"
                    >
                        Reset
                    </button>
                )}
            </div>
            
            {type === 'select' ? (
                <select value={value} onChange={(e) => onChange(e.target.value)} className={`w-full rounded px-2 py-1 text-xs outline-none focus:border-blue-500 ${inputStyleClass} bg-white`}>
                    {options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
            ) : type === 'boolean' ? (
                <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="rounded" />
            ) : type === 'color' ? (
                 <PropertyControl 
                    def={{ type: 'color' }}
                    value={value}
                    onChange={onChange}
                 />
            ) : (
                <input 
                    type={type} 
                    step={step} 
                    value={value === undefined || value === null ? '' : value} 
                    onChange={(e) => onChange(type === 'number' ? (e.target.value === '' ? undefined : parseFloat(e.target.value)) : e.target.value)} 
                    className={`w-full rounded px-2 py-1 text-xs outline-none focus:border-blue-500 ${inputStyleClass} ${isInherited ? 'bg-slate-50' : 'bg-white'}`}
                />
            )}
        </div>
      );
};

export default FieldRenderer;
